import React, { useState } from "react";
import { ReactComponent as SubtractLogo } from "../assets/Subtract.svg";
import PremiumInput from "../components/ui/PremiumInput";
import { useAuth } from "../hooks";
import { UI_TEXT } from "../constants/uiText";
import { loginSchema } from "../validation/schemas";

interface SignInViewProps {
  onLogin: (view: string, admin: boolean) => void;
  onSwitchToSignUp: () => void;
}

const SignInView: React.FC<SignInViewProps> = ({
  onLogin,
  onSwitchToSignUp,
}) => {
  const [loginId, setLoginId] = useState("");
  const [password, setPassword] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);
  const { login, isLoading, error } = useAuth();

  const handleLogin = async () => {
    const result = loginSchema.safeParse({ login_id: loginId, password });
    if (!result.success) {
      setValidationError(result.error.issues[0].message);
      return;
    }
    setValidationError(null);
    const { ok, isAdmin: admin } = await login({ login_id: loginId, password });
    if (ok) onLogin("home", admin);
  };

  return (
    <div className="flex flex-col h-full min-h-viewport px-6 py-12 bg-gradient-to-b from-black to-slate-900 relative animate-in fade-in duration-300 overflow-y-auto">
      <div className="flex-1 flex flex-col justify-center max-w-sm mx-auto w-full text-center">
        <div className="mb-10 text-center">
          <div className="flex items-center justify-center mx-auto mb-6">
            <SubtractLogo className="w-12 h-[70px] invert" />
          </div>
          <h1 className="text-3xl font-black tracking-tight mb-2 uppercase bg-gradient-to-r from-white via-white to-slate-400 bg-clip-text text-transparent">
            {UI_TEXT.common.appName}
          </h1>
          <p className="text-slate-400 text-xs font-bold tracking-widest uppercase">
            {UI_TEXT.auth.signIn.welcome}
          </p>
        </div>
        <div className="mb-6">
          <PremiumInput
            type="text"
            autoComplete="username"
            placeholder={UI_TEXT.auth.signIn.loginIdPlaceholder}
            label={UI_TEXT.auth.signIn.loginIdLabel}
            value={loginId}
            onChange={(e) => setLoginId(e.target.value)}
          />
          <PremiumInput
            type="password"
            autoComplete="current-password"
            placeholder={UI_TEXT.auth.signIn.passwordPlaceholder}
            label={UI_TEXT.auth.signIn.passwordLabel}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {(validationError || error) && (
            <p className="text-rose-500 text-xs font-bold text-left pl-1 -mt-2 mb-2">
              {validationError ?? error}
            </p>
          )}
          <button
            onClick={handleLogin}
            disabled={isLoading || !loginId.trim() || !password.trim()}
            className="w-full bg-white text-slate-900 font-bold py-4 rounded-lg shadow-lg mt-8 active:scale-95 transition-all uppercase tracking-widest disabled:opacity-50 disabled:bg-white/50"
          >
            {isLoading ? UI_TEXT.common.loading : UI_TEXT.auth.signIn.submit}
          </button>
          <div className="mt-8 pt-6 border-t border-white/20">
            <p className="text-slate-400 text-[10px] font-bold tracking-widest uppercase mb-3 text-center">
              {UI_TEXT.auth.signIn.noAccount}
            </p>
            <button
              onClick={onSwitchToSignUp}
              className="w-full bg-white border border-slate-200 text-slate-800 font-bold py-3.5 rounded-lg shadow-sm hover:bg-slate-50 transition-all uppercase tracking-widest"
            >
              {UI_TEXT.auth.signIn.toSignUp}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignInView;
